"use client";

import { createContext, useContext, type CSSProperties } from "react";
import type { RelayDrawerDirection, RelayTheme } from "../types";

/**
 * Context shared between `ResponsiveShell`, `ShellTrigger`, and `ShellContent`
 * — mirrors `WalletConnectModalContextValue`
 * (`packages/dapp-ui/src/features/wallet-connect-modal/providers/wallet-connect-modal-provider.ts`)
 * scoped down to only what the shared shell needs (compound components own
 * their own body content; the shell owns open state, viewport, and theme).
 *
 * `portalStyle` mirrors `WalletConnectModalContextValue.portalStyle`: radix's
 * `Dialog.Portal`/vaul's `Drawer.Portal` teleport their content to
 * `document.body`, outside the wrapper div's DOM subtree, so the merged
 * size-var style computed in `ResponsiveShell` must be re-applied directly on
 * the portaled `Dialog.Content`/`Drawer.Content` node — the wrapper's inline
 * style/CSS custom properties never reach it via cascade.
 */
export interface RelayShellContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  isMobile: boolean;
  theme: RelayTheme;
  drawerDirection: RelayDrawerDirection;
  portalStyle: CSSProperties;
}

export const RelayShellContext = createContext<RelayShellContextValue | null>(
  null,
);

export function useRelayShellContext(): RelayShellContextValue {
  const ctx = useContext(RelayShellContext);
  if (!ctx) {
    throw new Error(
      "ResponsiveShell compound components must be used within <ResponsiveShell>",
    );
  }
  return ctx;
}
